import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pencil, Plus, Trash2, MapPin, User as UserIcon, Lock, LogOut, AlertTriangle, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import AddressForm from "@/components/AddressForm";

// Inline AddressForm telah dipindah ke /components/AddressForm.jsx supaya
// dipakai bersama oleh Profile dan Checkout (single source of truth).

export default function ProfilePage() {
  const { user, loading: authLoading, updateProfile, logout, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", birth_date: "" });
  const [addresses, setAddresses] = useState([]);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [editingAddr, setEditingAddr] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/profile"); return; }
    setForm({ name: user.name || "", phone: user.phone || "", birth_date: user.birth_date || "" });
    api.get("/addresses").then((r) => setAddresses(r.data));
  }, [user, navigate, authLoading]);

  if (authLoading) return <div className="p-12 text-center text-[#5C5C5C]">Memuat...</div>;
  if (!user) return null;

  const saveProfile = async (e) => {
    e.preventDefault();
    try { await updateProfile(form); toast.success("Profil diperbarui"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", pwForm);
      toast.success("Password berhasil diubah");
      setPwForm({ current_password: "", new_password: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reload = async () => {
    const r = await api.get("/addresses");
    setAddresses(r.data);
    setOpen(false);
    setEditingAddr(null);
  };
  const delAddr = async (id) => {
    if (!confirm("Hapus alamat ini?")) return;
    await api.delete(`/addresses/${id}`);
    reload();
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <div className="rounded-3xl p-6 text-white mb-6" style={{ background: "linear-gradient(135deg, var(--brand-primary), color-mix(in srgb, var(--brand-primary) 70%, black))" }}>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur grid place-items-center font-display font-black text-2xl">
            {(user.name || user.email)[0].toUpperCase()}
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black">{user.name || "Pelanggan"}</h1>
            <div className="text-sm text-white/90">{user.email}</div>
            <div className="text-xs uppercase tracking-widest text-white/80 mt-1">{user.role}</div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="bg-white border border-[#E5E5E5] rounded-xl p-1 grid grid-cols-3 sm:flex w-full sm:w-auto">
          <TabsTrigger value="profile" className="data-[state=active]:brand-active-menu rounded-lg"><UserIcon size={14} className="mr-1" /> Profil</TabsTrigger>
          <TabsTrigger value="addresses" className="data-[state=active]:brand-active-menu rounded-lg"><MapPin size={14} className="mr-1" /> Alamat</TabsTrigger>
          <TabsTrigger value="password" className="data-[state=active]:brand-active-menu rounded-lg"><Lock size={14} className="mr-1" /> Password</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <form onSubmit={saveProfile} className="bg-white border border-[#E5E5E5] rounded-2xl p-5 space-y-4 max-w-xl">
            <div><Label>Username</Label><Input value={user.username || "(belum di-set)"} disabled className="rounded-xl bg-[#FAF9F6]" data-testid="profile-username-display" /></div>
            <div><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="profile-name-input" /></div>
            <div><Label>Email</Label><Input value={user.email} disabled className="rounded-xl bg-[#FAF9F6]" /></div>
            <div><Label>No. WhatsApp</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" data-testid="profile-phone-input" /></div>
            <div><Label>Tanggal Lahir</Label><Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="rounded-xl" /></div>
            <div className="flex gap-2">
              <Button type="submit" className="brand-btn-primary rounded-xl" data-testid="profile-save-btn">Simpan Profil</Button>
              <Button type="button" variant="outline" onClick={async () => { await logout(); navigate("/"); }} className="rounded-xl">
                <LogOut size={14} className="mr-1" /> Keluar
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="addresses" className="mt-4">
          {addresses.some((a) => !a.area_id) && (
            <div className="mb-3 p-3 rounded-xl border border-amber-300 bg-amber-50 flex items-start gap-2 text-sm" data-testid="relink-banner">
              <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-amber-900">Wilayah alamat belum lengkap</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  {addresses.filter((a) => !a.area_id).length} alamat belum memiliki wilayah lengkap.
                  Edit alamat dan lengkapi Provinsi, Kota, Kecamatan, dan Kelurahan agar ongkos kirim terhitung otomatis saat checkout.
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm text-[#5C5C5C]">{addresses.length} alamat tersimpan</div>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditingAddr(null); }}>
              <DialogTrigger asChild><Button className="brand-btn-primary rounded-xl" data-testid="add-address-btn"><Plus size={14} /> Tambah Alamat</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingAddr ? "Edit" : "Tambah"} Alamat</DialogTitle></DialogHeader>
                <AddressForm initial={editingAddr} onSaved={reload} />
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {addresses.map((a) => (
              <div key={a.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 relative" data-testid={`address-card-${a.id}`}>
                <div className="absolute top-3 right-3 flex flex-wrap gap-1 justify-end max-w-[60%]">
                  {a.is_default && <span className="text-[10px] brand-soft-bg brand-text-primary font-bold px-2 py-0.5 rounded" data-testid="addr-default-badge">UTAMA</span>}
                  {!a.area_id && (
                    <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 font-bold px-2 py-0.5 rounded" data-testid="addr-needs-relink">PERLU DILENGKAPI</span>
                  )}
                  {a.coordinate
                    ? <span className="text-[10px] bg-[#2D6A4F]/10 text-[#2D6A4F] font-bold px-2 py-0.5 rounded flex items-center gap-1" data-testid={`addr-pin-ok-${a.id}`}><MapPin size={10} /> PIN AKTIF</span>
                    : <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 font-bold px-2 py-0.5 rounded flex items-center gap-1" data-testid={`addr-pin-missing-${a.id}`}><MapPin size={10} /> BELUM PIN</span>
                  }
                </div>
                <div className="font-bold pr-24">{a.label} · {a.recipient_name}</div>
                <div className="text-sm text-[#5C5C5C]">{a.phone}</div>
                <div className="text-sm text-[#5C5C5C] mt-1">{a.address}, {a.city} {a.postal_code}</div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => { setEditingAddr(a); setOpen(true); }} className="rounded-lg" data-testid={`addr-edit-${a.id}`}><Pencil size={12} /></Button>
                  <Button size="sm" variant="outline" onClick={() => delAddr(a.id)} className="rounded-lg brand-text-primary" data-testid={`addr-delete-${a.id}`}><Trash2 size={12} /></Button>
                </div>
              </div>
            ))}
            {addresses.length === 0 && <div className="md:col-span-2 bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center text-[#5C5C5C]">Belum ada alamat tersimpan</div>}
          </div>
        </TabsContent>

        <TabsContent value="password" className="mt-4">
          <form onSubmit={changePw} className="bg-white border border-[#E5E5E5] rounded-2xl p-5 space-y-4 max-w-md">
            <div><Label>Password Lama</Label><Input value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} type="password" required className="rounded-xl" /></div>
            <div><Label>Password Baru</Label><Input value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} type="password" minLength={6} required className="rounded-xl" /></div>
            <Button type="submit" className="brand-btn-primary rounded-xl">Ubah Password</Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}
